/**
 * CodeMirror 6 Editor — CSP-compatible advanced code editor
 *
 * Provides syntax highlighting, autocomplete, and rich editing for OpenSCAD
 * without requiring style-src 'unsafe-inline'. CodeMirror itself injects its
 * CSS in a <style> element, which a strict style-src discards; the rules are
 * re-homed into a constructable stylesheet by codemirror-csp-styles.js.
 *
 * Public API matches TextareaEditor for drop-in substitution:
 *   constructor({ container, onChange, onSave, onRun, announce })
 *   initialize(), getValue(), setValue(v), focus(), dispose()
 *   getSelection(), setSelection(s,e), setCursorPosition(l,c), scrollToLine(l)
 *   setErrorLines(lines), clearErrors(), supportsAction(id),
 *   performAction(id), canUndo(), canRedo(), replaceSelection(text)
 *
 * @license GPL-3.0-or-later
 */

import {
  EditorView,
  keymap,
  lineNumbers,
  highlightActiveLine,
  drawSelection,
  Decoration,
  gutter,
  GutterMarker,
} from '@codemirror/view';
import {
  EditorState,
  Compartment,
  StateEffect,
  StateField,
  RangeSet,
} from '@codemirror/state';
import {
  StreamLanguage,
  syntaxHighlighting,
  HighlightStyle,
  codeFolding,
  foldKeymap,
  foldAll,
  unfoldAll,
  foldService,
  indentUnit,
  bracketMatching,
} from '@codemirror/language';
import { tags } from '@lezer/highlight';
import {
  defaultKeymap,
  history,
  historyKeymap,
  indentMore,
  indentLess,
  lineComment,
  lineUncomment,
  undo,
  redo,
  undoDepth,
  redoDepth,
} from '@codemirror/commands';
import { autocompletion } from '@codemirror/autocomplete';
import {
  search,
  highlightSelectionMatches,
  searchKeymap,
  openSearchPanel,
  findNext,
  findPrevious,
  setSearchQuery,
  SearchQuery,
} from '@codemirror/search';
import { adoptCodeMirrorStyles } from './codemirror-csp-styles.js';
import { wrapIndent, wrapReturnArrows } from './editor-wrap-marks.js';
import { boxedFoldGutter } from './editor-fold-markers.js';
import { LIGHT_SCHEME, DARK_SCHEME } from './editor-color-scheme.js';
import { loadEditorPrefs } from './editor-prefs.js';
import { themeManager } from './theme-manager.js';

/**
 * Where the editor's dark mode comes from: the app, never the OS media
 * query. Classic is always light — its chrome is the desktop token remap and
 * the desktop editor is white. Every other mode follows the resolved app
 * theme; 'auto' keeps data-theme synced to the OS, so auto still works
 * through this path.
 *
 * @param {{ uiMode: string|null, resolvedTheme: string|null }} inputs
 * @returns {boolean}
 */
export function resolveEditorDarkMode({ uiMode, resolvedTheme }) {
  if (uiMode === 'classic') return false;
  return resolvedTheme === 'dark';
}

// ─── OpenSCAD token lists (ported from textarea-editor.js / monaco-editor.js) ──

const SCAD_KEYWORDS = new Set([
  'module',
  'function',
  'if',
  'else',
  'for',
  'let',
  'each',
  'intersection_for',
  'assert',
  'echo',
  'include',
  'use',
]);

const SCAD_BUILTINS = new Set([
  'cube',
  'sphere',
  'cylinder',
  'polyhedron',
  'circle',
  'square',
  'polygon',
  'text',
  'linear_extrude',
  'rotate_extrude',
  'surface',
  'import',
  'union',
  'difference',
  'intersection',
  'hull',
  'minkowski',
  'translate',
  'rotate',
  'scale',
  'mirror',
  'multmatrix',
  'color',
  'offset',
  'resize',
  'projection',
  'render',
  'children',
]);

const SCAD_FUNCTIONS = new Set([
  'abs',
  'sign',
  'sin',
  'cos',
  'tan',
  'acos',
  'asin',
  'atan',
  'atan2',
  'floor',
  'round',
  'ceil',
  'ln',
  'log',
  'pow',
  'sqrt',
  'exp',
  'rands',
  'min',
  'max',
  'concat',
  'lookup',
  'str',
  'chr',
  'ord',
  'search',
  'version',
  'version_num',
  'len',
  'norm',
  'cross',
  'is_undef',
  'is_bool',
  'is_num',
  'is_string',
  'is_list',
  'is_function',
]);

const SCAD_CONSTANTS = new Set(['true', 'false', 'undef', 'PI']);

// ─── StreamLanguage tokenizer ───────────────────────────────────────────────

const openscadStreamLanguage = StreamLanguage.define({
  startState() {
    return { inBlockComment: false };
  },

  token(stream, state) {
    if (state.inBlockComment) {
      if (stream.match('*/')) {
        state.inBlockComment = false;
      } else {
        stream.next();
      }
      return 'blockComment';
    }

    if (stream.match('/*')) {
      state.inBlockComment = true;
      return 'blockComment';
    }

    if (stream.match(/\/\/.*/)) {
      return 'lineComment';
    }

    if (stream.match(/"(?:[^"\\]|\\.)*"/)) {
      return 'string';
    }

    if (
      stream.match(/\d+\.?\d*(?:[eE][+-]?\d+)?/) ||
      stream.match(/\.\d+(?:[eE][+-]?\d+)?/)
    ) {
      return 'number';
    }

    if (stream.match(/\$[a-zA-Z_]\w*/)) {
      return 'special';
    }

    if (stream.match(/[a-zA-Z_]\w*/)) {
      const word = stream.current();
      if (SCAD_KEYWORDS.has(word)) return 'keyword';
      if (SCAD_BUILTINS.has(word)) return 'builtin';
      if (SCAD_FUNCTIONS.has(word)) return 'function';
      if (SCAD_CONSTANTS.has(word)) return 'constant';
      return 'variable';
    }

    // Operators and punctuation, which the desktop draws in its operator
    // color, brackets and semicolons included. Without this match they fall
    // through to `null` and paint as plain text.
    if (stream.match(/[+\-*/%=<>!&|?:;,.()[\]{}]/)) {
      return 'operator';
    }

    stream.next();
    return null;
  },

  // Without this, four of the tokens above reach no tag at all: builtins,
  // functions, constants and $-variables would render as plain text.
  // `builtin` is the only one CodeMirror's legacy table knows; `function`,
  // `constant` and `special` are modifier names rather than tags, so on
  // their own they resolve to nothing.
  tokenTable: {
    builtin: tags.standard(tags.variableName),
    function: tags.function(tags.variableName),
    constant: tags.atom,
    special: tags.special(tags.variableName),
  },

  languageData: {
    commentTokens: { line: '//', block: { open: '/*', close: '*/' } },
  },
});

// ─── Highlight styles (light + dark) ────────────────────────────────────────
//
// Built from the desktop's own editor schemes. The mapping from the scheme
// file's keys to this tokenizer's tokens, checked against desktop
// screenshots rather than assumed:
//
//   keyword1  Green      language keywords     `module` renders green
//   keyword2  Green      constants             true / false / undef / PI
//   keyword3  DarkBlue   builtins, functions,  `difference` and `union`
//                        and $-variables       render dark blue
//   comment   DarkCyan   line + block comments
//   number    DarkRed
//   string    DarkMagenta
//   operator  Blue       brackets and punctuation included
//
// $-variables are keyword3 by inference (no screenshot showed one) and are
// grouped with the builtins they belong to. Everything else was read off
// the pixels.
//
// Neither bold nor italic is applied: the desktop renders keywords and
// comments at the same weight and slope as everything else.

/**
 * @param {import('./editor-color-scheme.js').EditorScheme} scheme
 * @returns {import('@codemirror/language').HighlightStyle}
 */
function highlightStyleFor(scheme) {
  const t = scheme.tokens;
  return HighlightStyle.define([
    { tag: tags.keyword, color: t.keyword1 },
    { tag: tags.atom, color: t.keyword2 },
    { tag: tags.standard(tags.variableName), color: t.keyword3 },
    { tag: tags.function(tags.variableName), color: t.keyword3 },
    { tag: tags.special(tags.variableName), color: t.keyword3 },
    { tag: tags.lineComment, color: t.comment },
    { tag: tags.blockComment, color: t.comment },
    { tag: tags.comment, color: t.comment },
    { tag: tags.string, color: t.string },
    { tag: tags.number, color: t.number },
    { tag: tags.operator, color: t.operator },
  ]);
}

const lightHighlightStyle = highlightStyleFor(LIGHT_SCHEME);
const darkHighlightStyle = highlightStyleFor(DARK_SCHEME);

/**
 * The Alt View's scheme: every value is one of the variant's own tokens, so
 * the green and the amber phosphor both follow, and the consistency spec
 * finds nothing outside the palette. A phosphor screen has one hue, so the
 * tokens tell the kinds of text apart by brightness: keywords brightest,
 * code at the text color, comments and strings a step dimmer. The gutter
 * and the caret line are the variant's two darker surfaces.
 * @type {import('./editor-color-scheme.js').EditorScheme}
 */
const MONO_SCHEME = Object.freeze({
  paper: 'var(--color-bg-primary)',
  text: 'var(--color-text-primary)',
  caretLine: 'var(--color-bg-tertiary)',
  marginBackground: 'var(--color-bg-secondary)',
  marginForeground: 'var(--color-text-secondary)',
  selectionBackground: 'var(--color-hover-bg)',
  selectionForeground: 'var(--color-text-primary)',
  matchedBraceBackground: 'var(--color-accent-subtle)',
  matchedBraceForeground: 'var(--color-accent-hover)',
  unmatchedBraceBackground: 'var(--color-error-bg)',
  unmatchedBraceForeground: 'var(--color-error)',
  tokens: Object.freeze({
    keyword1: 'var(--color-accent-hover)',
    keyword2: 'var(--color-text-primary)',
    keyword3: 'var(--color-text-primary)',
    comment: 'var(--color-text-secondary)',
    number: 'var(--color-text-primary)',
    string: 'var(--color-text-secondary)',
    operator: 'var(--color-text-primary)',
  }),
});
const monoHighlightStyle = highlightStyleFor(MONO_SCHEME);

/** @returns {boolean} True while the page wears the Alt View's variant */
function isMonoVariant() {
  return (
    typeof document !== 'undefined' &&
    document.documentElement.getAttribute('data-ui-variant') === 'mono'
  );
}

/**
 * CodeMirror has no font-size facility; a theme is the facility. Set on the
 * editor root and on .cm-gutters so the line numbers scale with the code —
 * sizing only the content leaves the gutter behind and the two stop lining
 * up. Given in px because the control is a px control (Edit ▸ Font Size has
 * always announced "Font size: 14px").
 *
 * The explicit line-height is not decoration. Inherited from the page, the
 * unitless line-height recomputes against the root's font size: at 14px
 * rows fall to 20px, a ratio of 1.43, under the 1.5 that WCAG 2.2 SC 1.4.12
 * Text Spacing asks for. Pinning it here keeps the ratio at every font size
 * the user can choose, instead of letting it drift out of range whenever
 * the size changes.
 *
 * @param {number} px
 */
const EDITOR_LINE_HEIGHT = 1.6;

function fontSizeTheme(px) {
  return EditorView.theme({
    '&': { fontSize: `${px}px` },
    // The row height follows .cm-scroller, which carries CodeMirror's own
    // line-height: 1.4 — that is where the 1.43 came from, not the root.
    '.cm-scroller': { lineHeight: String(EDITOR_LINE_HEIGHT) },
    '.cm-gutters': { fontSize: `${px}px` },
  });
}

/**
 * The editor chrome, from the same scheme file as the syntax colors.
 *
 * `.cm-activeLine` takes the scheme's caret-line background verbatim,
 * which is why every foreground in editor-color-scheme.js is
 * contrast-checked against the caret line as well as the paper.
 *
 * @param {import('./editor-color-scheme.js').EditorScheme} scheme
 * @param {{dark?: boolean}} [options]
 */
function editorThemeFor(scheme, options = {}) {
  return EditorView.theme(
    {
      '&': {
        backgroundColor: scheme.paper,
        color: scheme.text,
      },
      '.cm-gutters': {
        backgroundColor: scheme.marginBackground,
        color: scheme.marginForeground,
        borderRight: `1px solid ${options.gutterBorder ?? (options.dark ? '#333' : '#ddd')}`,
      },
      '.cm-activeLineGutter': {
        color: scheme.text,
        backgroundColor: scheme.caretLine,
      },
      '&.cm-focused .cm-selectionBackground, .cm-selectionBackground': {
        backgroundColor: scheme.selectionBackground,
      },
      '.cm-selectionMatch': {
        backgroundColor: scheme.matchedBraceBackground,
      },
      '.cm-activeLine': {
        backgroundColor: scheme.caretLine,
      },
      // Brace matching. The scheme carries both pairs, so it needs no color of
      // its own.
      '.cm-matchingBracket, &.cm-focused .cm-matchingBracket': {
        backgroundColor: scheme.matchedBraceBackground,
        color: scheme.matchedBraceForeground,
      },
      '.cm-nonmatchingBracket, &.cm-focused .cm-nonmatchingBracket': {
        backgroundColor: scheme.unmatchedBraceBackground,
        color: scheme.unmatchedBraceForeground,
      },
    },
    options
  );
}

const lightEditorTheme = editorThemeFor(LIGHT_SCHEME);
const darkEditorTheme = editorThemeFor(DARK_SCHEME, { dark: true });
const monoEditorTheme = [
  editorThemeFor(MONO_SCHEME, {
    dark: true,
    gutterBorder: 'var(--color-border-light)',
  }),
  // CodeMirror marks focus with a dotted dark gray line of its own; the
  // variant marks it with its accent, as every other control does.
  EditorView.theme({
    '&.cm-focused': {
      outline: '2px solid var(--color-focus)',
      outlineOffset: '2px',
    },
  }),
];

// ─── Autocomplete for OpenSCAD ──────────────────────────────────────────────

function openscadCompletions(context) {
  const word = context.matchBefore(/[a-zA-Z_$]\w*/);
  if (!word || (word.from === word.to && !context.explicit)) return null;

  const options = [];

  for (const kw of SCAD_KEYWORDS) {
    options.push({ label: kw, type: 'keyword' });
  }
  for (const bi of SCAD_BUILTINS) {
    options.push({ label: bi, type: 'function', apply: bi + '()' });
  }
  for (const fn of SCAD_FUNCTIONS) {
    options.push({ label: fn, type: 'function', apply: fn + '()' });
  }
  for (const c of SCAD_CONSTANTS) {
    options.push({ label: c, type: 'constant' });
  }

  return { from: word.from, options };
}

// ─── Error line state (StateEffect + StateField) ────────────────────────────

const setErrorEffect = StateEffect.define();

const errorLineField = StateField.define({
  create() {
    return Decoration.none;
  },
  update(decorations, tr) {
    for (const effect of tr.effects) {
      if (effect.is(setErrorEffect)) {
        return effect.value;
      }
    }
    return decorations.map(tr.changes);
  },
  provide: (f) => EditorView.decorations.from(f),
});

const errorLineTheme = EditorView.baseTheme({
  '.cm-error-line': {
    backgroundColor: 'rgba(255, 0, 0, 0.1)',
    outline: '1px solid rgba(255, 0, 0, 0.3)',
  },
});

// ─── Bookmarks ───────────────────────────────────────────────────────────────
// CodeMirror has no bookmark feature, so this is a small line-marker extension:
// a gutter dot per bookmarked line, plus toggle/next/previous commands. The
// marker is decorative — the state reaches a screen reader through the
// announcements in performAction(), not through the gutter.

class BookmarkMarker extends GutterMarker {
  toDOM() {
    const dot = document.createElement('span');
    dot.className = 'cm-bookmark-dot';
    dot.setAttribute('aria-hidden', 'true');
    dot.textContent = '●';
    return dot;
  }
}

const bookmarkMarker = new BookmarkMarker();

/** @type {StateEffectType<{line: number, on: boolean}>} */
const toggleBookmarkEffect = StateEffect.define();

const bookmarkField = StateField.define({
  create() {
    return RangeSet.empty;
  },
  update(marks, tr) {
    marks = marks.map(tr.changes);
    for (const effect of tr.effects) {
      if (!effect.is(toggleBookmarkEffect)) continue;
      const { doc } = tr.state;
      const lineNumber = effect.value.line;
      if (lineNumber < 1 || lineNumber > doc.lines) continue;
      if (effect.value.on) {
        marks = marks.update({
          add: [bookmarkMarker.range(doc.line(lineNumber).from)],
          sort: true,
        });
      } else {
        marks = marks.update({
          filter: (from) => lineNumberAt(doc, from) !== lineNumber,
        });
      }
    }
    return marks;
  },
});

function lineNumberAt(doc, pos) {
  return doc.lineAt(Math.max(0, Math.min(pos, doc.length))).number;
}

/**
 * Bookmarked line numbers, ascending. Edits can drag two marks onto the same
 * line, so the set is de-duplicated on read rather than on every change.
 * @param {EditorState} state
 * @returns {number[]}
 */
function bookmarkLines(state) {
  const lines = new Set();
  const iter = state.field(bookmarkField).iter();
  while (iter.value) {
    lines.add(lineNumberAt(state.doc, iter.from));
    iter.next();
  }
  return [...lines].sort((a, b) => a - b);
}

function cursorLine(state) {
  return state.doc.lineAt(state.selection.main.head).number;
}

const toggleBookmark = (view) => {
  const line = cursorLine(view.state);
  const on = !bookmarkLines(view.state).includes(line);
  view.dispatch({ effects: toggleBookmarkEffect.of({ line, on }) });
  return true;
};

function gotoBookmark(view, direction) {
  const lines = bookmarkLines(view.state);
  if (lines.length === 0) return false;
  const current = cursorLine(view.state);
  const target =
    direction > 0
      ? (lines.find((line) => line > current) ?? lines[0])
      : (lines.filter((line) => line < current).pop() ??
        lines[lines.length - 1]);
  const line = view.state.doc.line(target);
  view.dispatch({ selection: { anchor: line.from }, scrollIntoView: true });
  return true;
}

const nextBookmark = (view) => gotoBookmark(view, 1);
const previousBookmark = (view) => gotoBookmark(view, -1);

const bookmarkGutter = gutter({
  class: 'cm-bookmark-gutter',
  markers: (view) => view.state.field(bookmarkField),
  initialSpacer: () => bookmarkMarker,
});

const bookmarkTheme = EditorView.baseTheme({
  '.cm-bookmark-gutter': { width: '1em' },
  '.cm-bookmark-dot': { color: '#0b6bcb', fontSize: '0.7em', lineHeight: 1.6 },
  '&dark .cm-bookmark-dot': { color: '#78bafc' },
});

// ─── Code folding ────────────────────────────────────────────────────────────
// OpenSCAD is tokenized here by a StreamLanguage, which carries no structure —
// so foldGutter would draw nothing and Fold All would silently do nothing.
// This supplies the missing fold information: a foldable block is the text
// between a brace opened on a line and its match, with braces inside strings
// and comments ignored. Ranges are computed once per document version, since
// the fold service is asked about every visible line on every repaint.

const foldRangeCache = new WeakMap();

function computeFoldRanges(doc) {
  const text = doc.toString();
  const open = [];
  const pairs = [];
  let inLineComment = false;
  let inBlockComment = false;
  let inString = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const next = text[i + 1];

    if (inLineComment) {
      if (char === '\n') inLineComment = false;
      continue;
    }
    if (inBlockComment) {
      if (char === '*' && next === '/') {
        inBlockComment = false;
        i++;
      }
      continue;
    }
    if (inString) {
      if (char === '\\') i++;
      else if (char === '"') inString = false;
      continue;
    }
    if (char === '/' && next === '/') {
      inLineComment = true;
      i++;
    } else if (char === '/' && next === '*') {
      inBlockComment = true;
      i++;
    } else if (char === '"') {
      inString = true;
    } else if (char === '{') {
      open.push(i);
    } else if (char === '}' && open.length > 0) {
      pairs.push([open.pop(), i]);
    }
  }

  const byLine = new Map();
  for (const [from, to] of pairs) {
    const startLine = doc.lineAt(from).number;
    // A block that opens and closes on one line has nothing to hide.
    if (doc.lineAt(to).number === startLine) continue;
    const existing = byLine.get(startLine);
    if (!existing || to > existing.to) {
      byLine.set(startLine, { from: from + 1, to });
    }
  }
  return byLine;
}

function foldRangesFor(doc) {
  let ranges = foldRangeCache.get(doc);
  if (!ranges) {
    ranges = computeFoldRanges(doc);
    foldRangeCache.set(doc, ranges);
  }
  return ranges;
}

const scadFoldService = foldService.of((state, lineStart) => {
  const line = state.doc.lineAt(lineStart);
  return foldRangesFor(state.doc).get(line.number) ?? null;
});

// ─── Whitespace and search helpers ───────────────────────────────────────────

/**
 * Expand every tab to the next tab stop, so the visible layout is unchanged.
 * A flat "one tab becomes N spaces" swap would shift indented code.
 */
const convertTabsToSpaces = (view) => {
  const { state } = view;
  if (!state.doc.toString().includes('\t')) return false;
  const tabSize = state.tabSize || 4;
  const changes = [];
  for (let n = 1; n <= state.doc.lines; n++) {
    const line = state.doc.line(n);
    if (!line.text.includes('\t')) continue;
    let column = 0;
    let expanded = '';
    for (const char of line.text) {
      if (char === '\t') {
        const width = tabSize - (column % tabSize);
        expanded += ' '.repeat(width);
        column += width;
      } else {
        expanded += char;
        column += 1;
      }
    }
    changes.push({ from: line.from, to: line.to, insert: expanded });
  }
  if (changes.length === 0) return false;
  view.dispatch({ changes });
  return true;
};

/**
 * Seed the search query from the selection and move to the next match, which
 * is what upstream's Use Selection for Find does. This only works because the
 * search() extension is installed below: without it CodeMirror adds its
 * search state field lazily when the panel first opens, so setSearchQuery
 * would be silently dropped and findNext would just open an empty panel.
 */
const useSelectionForFind = (view) => {
  const range = view.state.selection.main;
  if (range.empty) return false;
  view.dispatch({
    effects: setSearchQuery.of(
      new SearchQuery({ search: view.state.sliceDoc(range.from, range.to) })
    ),
  });
  return findNext(view);
};

// ─── CodeMirrorEditor class ─────────────────────────────────────────────────

// Named editor commands exposed to the Edit menu / keyboard shortcuts.
// All are stock CodeMirror commands; comment syntax comes from the
// commentTokens languageData on the OpenSCAD stream language above.
const EDITOR_COMMANDS = {
  // Text undo/redo, distinct from the app's parameter history. A full-document
  // programmatic setValue resets this history on purpose, so Undo can
  // never resurrect a previously loaded project.
  undo,
  redo,
  indent: indentMore,
  unindent: indentLess,
  comment: lineComment,
  uncomment: lineUncomment,
  find: openSearchPanel,
  // CodeMirror's search panel includes the replace controls, so Find and
  // Replace needs no separate panel.
  findReplace: openSearchPanel,
  findNext,
  findPrevious,
  useSelectionForFind,
  convertTabsToSpaces,
  toggleBookmark,
  nextBookmark,
  previousBookmark,
  foldAll,
  unfoldAll,
};

export class CodeMirrorEditor {
  /**
   * @param {Object} options
   * @param {HTMLElement} options.container
   * @param {Function} options.onChange
   * @param {Function} options.onSave
   * @param {Function} options.onRun
   * @param {Function} options.announce - Screen reader announcement function
   */
  constructor(options = {}) {
    /** @type {HTMLElement} */
    this.container = options.container;

    /** @type {Function} */
    this.onChange = options.onChange || (() => {});

    /** @type {Function} */
    this.onSave = options.onSave || (() => {});

    /** @type {Function} */
    this.onRun = options.onRun || (() => {});

    /** @type {Function} */
    this.announce = options.announce || (() => {});

    /** @type {EditorView|null} */
    this._view = null;

    /** @type {Compartment} */
    this._themeCompartment = new Compartment();

    /** @type {Compartment} */
    this._highlightCompartment = new Compartment();

    /** @type {Compartment} - Holds history() so setValue can reset undo state */
    this._historyCompartment = new Compartment();

    // Preferences ▸ Editor reconfigures these live. Each is a real CodeMirror
    // facility: a theme for the font size, the indentUnit and tabSize facets,
    // the lineWrapping extension and highlightActiveLine. Anything the tab
    // offers that has no facility behind it ships disabled with a reason
    // rather than pretending.
    /** @type {Compartment} */
    this._fontSizeCompartment = new Compartment();
    /** @type {Compartment} */
    this._indentCompartment = new Compartment();
    /** @type {Compartment} */
    this._tabSizeCompartment = new Compartment();
    /** @type {Compartment} */
    this._wrapCompartment = new Compartment();
    /** @type {Compartment} */
    this._wrapIndentCompartment = new Compartment();
    /** @type {Compartment} */
    this._wrapArrowCompartment = new Compartment();
    /** @type {Compartment} */
    this._braceMatchCompartment = new Compartment();
    /** @type {Compartment} */
    this._activeLineCompartment = new Compartment();
    /** @type {Compartment} */
    this._autocompleteCompartment = new Compartment();

    /** @type {import('./editor-prefs.js').EditorPrefs} */
    this._editorPrefs = loadEditorPrefs();

    /**
     * True while setValue() replaces the document. A programmatic replace is
     * not a user edit: it must not reach onChange, or loading a project marks
     * the buffer dirty before the user has typed anything.
     * @type {boolean}
     */
    this._suppressOnChange = false;

    /** @type {Set<number>} */
    this._errorLines = new Set();

    /** @type {boolean} */
    this._isInitialized = false;

    /** @type {Function|null} Unsubscribe from themeManager's change feed */
    this._unsubscribeTheme = null;

    /** @type {Function|null} */
    this._uiModeListener = null;

    /** @type {MutationObserver|null} Follows the root's Alt View attribute */
    this._variantObserver = null;

    /** @type {ResizeObserver|null} */
    this._resizeObserver = null;
  }

  initialize() {
    if (this._isInitialized) return;

    const isDark = this._resolveIsDark();

    const onSave = this.onSave;
    const onRun = this.onRun;

    // Re-read on every initialize(): the editor is destroyed and rebuilt on
    // each mode switch, so settings applied in a previous life have to come
    // back from storage or they silently reset.
    this._editorPrefs = loadEditorPrefs();

    const startState = EditorState.create({
      doc: '',
      extensions: [
        // Desktop OpenSCAD wraps at word boundaries by default. Without this
        // a long line runs off the pane and has to be scrolled to sideways,
        // which also fails WCAG 1.4.10. CodeMirror keeps one line number per
        // logical line, against its first visual row, as the desktop does.
        this._wrapCompartment.of(
          this._editorPrefs.lineWrapping ? EditorView.lineWrapping : []
        ),
        // How a wrapped line tells you it is one: continuation rows hang four
        // columns in and every row that continues carries a return arrow at
        // the right border, both from the desktop's own defaults. Neither
        // mark is made of characters, so the document is untouched. Two
        // compartments because the desktop keeps them as two settings and
        // this mirrors that.
        this._wrapIndentCompartment.of(
          this._editorPrefs.wrapIndent ? wrapIndent() : []
        ),
        this._wrapArrowCompartment.of(
          this._editorPrefs.wrapArrow ? wrapReturnArrows() : []
        ),
        this._fontSizeCompartment.of(fontSizeTheme(this._editorPrefs.fontSize)),
        this._indentCompartment.of(
          indentUnit.of(' '.repeat(this._editorPrefs.indentWidth))
        ),
        this._tabSizeCompartment.of(
          EditorState.tabSize.of(this._editorPrefs.tabWidth)
        ),
        lineNumbers(),
        bookmarkField,
        bookmarkGutter,
        bookmarkTheme,
        codeFolding(),
        scadFoldService,
        boxedFoldGutter(),
        // The desktop highlights the brace matching the one at the
        // cursor, and its scheme file already carried both color pairs. It is
        // a real setting there (settings.cc enableBraceMatching, default true)
        // sitting beside highlightCurrentLine, so it is a real setting here.
        this._braceMatchCompartment.of(
          this._editorPrefs.braceMatching ? bracketMatching() : []
        ),
        this._historyCompartment.of(history()),
        drawSelection(),
        this._activeLineCompartment.of(
          this._editorPrefs.highlightActiveLine ? highlightActiveLine() : []
        ),
        highlightSelectionMatches(),
        // Installed explicitly so the search state exists from the start.
        // CodeMirror otherwise adds it only when the panel first opens, and
        // Use Selection for Find sets its query before that ever happens.
        search(),

        openscadStreamLanguage,
        this._highlightCompartment.of(
          syntaxHighlighting(this._highlightStyleFor(isDark))
        ),
        this._themeCompartment.of(this._editorThemeFor(isDark)),

        this._autocompleteCompartment.of(
          this._editorPrefs.autocomplete
            ? autocompletion({ override: [openscadCompletions] })
            : []
        ),

        keymap.of([
          // Neither shortcut announces here: the keymap cannot know the
          // outcome. Saving announces "Project saved" once it succeeds and
          // toasts on failure; the preview state indicator is an aria-live
          // region that reports rendering and readiness on its own.
          {
            key: 'Mod-s',
            run() {
              onSave();
              return true;
            },
          },
          {
            key: 'Mod-Enter',
            run() {
              onRun();
              return true;
            },
          },
          // Upstream's bookmark keys (MainWindow.ui). They live in the
          // editor's own keymap rather than the app shortcut registry
          // because they only mean anything while the editor has focus.
          {
            key: 'Mod-F2',
            run: (view) => this._runAndAnnounce('toggleBookmark', view),
          },
          {
            key: 'F2',
            run: (view) => this._runAndAnnounce('nextBookmark', view),
          },
          {
            key: 'Shift-F2',
            run: (view) => this._runAndAnnounce('previousBookmark', view),
          },
          ...defaultKeymap,
          ...historyKeymap,
          ...searchKeymap,
          ...foldKeymap,
        ]),

        EditorView.updateListener.of((update) => {
          if (update.docChanged && !this._suppressOnChange) {
            this.onChange(update.state.doc.toString());
          }
        }),

        errorLineField,
        errorLineTheme,
      ],
    });

    this._view = new EditorView({
      state: startState,
      parent: this.container,
    });

    // The view has mounted its styles by now, so there is something to adopt.
    adoptCodeMirrorStyles();

    const cmContent = this._view.contentDOM;
    cmContent.setAttribute('aria-label', 'OpenSCAD code editor');

    this._unsubscribeTheme = themeManager.addListener(() =>
      this._switchTheme(this._resolveIsDark())
    );
    this._variantObserver = new MutationObserver(() => this.refreshTheme());
    this._variantObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-ui-variant'],
    });
    this._uiModeListener = () => this._switchTheme(this._resolveIsDark());
    document.addEventListener('ui-mode-changed', this._uiModeListener);

    this._observeContainerResize();

    this._isInitialized = true;
    console.log('[CodeMirrorEditor] Initialized');
  }

  /**
   * Re-measure when the pane changes width.
   *
   * CodeMirror already watches its own scroller, but the handler is guarded:
   * `if (view.docView.lastUpdate < Date.now() - 75) this.onResize()`
   * (@codemirror/view, the DOMObserver constructor). A resize that lands
   * within 75ms of a doc-view update is dropped and never retried, and
   * Classic's responsive reflow does exactly that — it re-lays the dock and
   * updates the editor in the same moment. Measured: dragging the viewport to
   * 768 took the editor from 213px to 701px of content while CodeMirror went
   * on believing the old width for as long as it was left alone, curing only
   * on the next click or window resize.
   *
   * This observer has no such guard, so the cached geometry cannot survive a
   * resize. `requestMeasure` coalesces into CodeMirror's own measure cycle, so
   * a drag costs one measure per frame rather than one per pixel.
   *
   * A measure on its own is not enough: view-level
   * extensions only recompute inside a ViewUpdate, and re-wrapping is done by
   * the browser in CSS without the doc view being redrawn, so no update is
   * produced. The empty transaction makes one. It carries no changes, and the
   * update listener above reacts only to `docChanged`, so nothing is marked
   * dirty and the undo history is untouched.
   *
   * @private
   */
  _observeContainerResize() {
    if (typeof ResizeObserver !== 'function' || !this.container) return;

    let lastWidth = -1;
    this._resizeObserver = new ResizeObserver(() => {
      const view = this._view;
      if (!view) return;
      const width = view.contentDOM.clientWidth;
      if (width === lastWidth) return;
      lastWidth = width;
      view.requestMeasure();
      view.dispatch({});
    });
    this._resizeObserver.observe(this.container);
  }

  /** @private @returns {boolean} */
  _resolveIsDark() {
    return resolveEditorDarkMode({
      uiMode: document.body?.dataset?.uiMode ?? null,
      resolvedTheme: themeManager.getResolvedTheme(),
    });
  }

  /** @private */
  _highlightStyleFor(isDark) {
    if (isMonoVariant()) return monoHighlightStyle;
    return isDark ? darkHighlightStyle : lightHighlightStyle;
  }

  /** @private */
  _editorThemeFor(isDark) {
    if (isMonoVariant()) return monoEditorTheme;
    return isDark ? darkEditorTheme : lightEditorTheme;
  }

  /** @private */
  _switchTheme(isDark) {
    if (!this._view) return;
    this._view.dispatch({
      effects: [
        this._themeCompartment.reconfigure(this._editorThemeFor(isDark)),
        this._highlightCompartment.reconfigure(
          syntaxHighlighting(this._highlightStyleFor(isDark))
        ),
      ],
    });
  }

  /**
   * Re-read the theme and the variant and apply them. The Alt View sets
   * the root's variant attribute without going through the theme manager,
   * so the editor watches that attribute itself (see initialize()).
   */
  refreshTheme() {
    this._switchTheme(this._resolveIsDark());
  }

  /** @returns {string} */
  getValue() {
    return this._view ? this._view.state.doc.toString() : '';
  }

  /**
   * Replace the whole document programmatically (project load, mode switch).
   * Not a user edit: onChange stays silent and the undo history is discarded,
   * so Undo cannot resurrect the previously loaded project — this matches
   * desktop OpenSCAD's behavior when opening a file.
   * @param {string} value
   */
  setValue(value) {
    if (!this._view) return;
    this._suppressOnChange = true;
    try {
      this._view.dispatch({
        changes: { from: 0, to: this._view.state.doc.length, insert: value },
      });
      this._resetHistory();
    } finally {
      this._suppressOnChange = false;
    }
  }

  /**
   * Discard undo/redo state by tearing the history field out of the
   * configuration and putting a fresh one back. Reconfiguring in a single
   * transaction would keep the existing field, so this needs two dispatches.
   * @private
   */
  _resetHistory() {
    if (!this._view) return;
    this._view.dispatch({ effects: this._historyCompartment.reconfigure([]) });
    this._view.dispatch({
      effects: this._historyCompartment.reconfigure(history()),
    });
  }

  focus() {
    if (this._view) {
      this._view.focus();
    }
  }

  // ---------------------------------------------------------------------------
  // Preferences ▸ Editor — live reconfiguration
  //
  // Each setter reconfigures one compartment on the running editor, so a
  // change is visible on the document already open rather than on the next
  // one. Values are clamped by editor-prefs before they arrive here.
  // ---------------------------------------------------------------------------

  /** @param {number} px */
  setFontSize(px) {
    this._editorPrefs.fontSize = px;
    this._view?.dispatch({
      effects: this._fontSizeCompartment.reconfigure(fontSizeTheme(px)),
    });
  }

  /** @param {number} width Spaces per indent step. */
  setIndentWidth(width) {
    this._editorPrefs.indentWidth = width;
    this._view?.dispatch({
      effects: this._indentCompartment.reconfigure(
        indentUnit.of(' '.repeat(width))
      ),
    });
  }

  /** @param {number} width Columns a literal tab character occupies. */
  setTabWidth(width) {
    this._editorPrefs.tabWidth = width;
    this._view?.dispatch({
      effects: this._tabSizeCompartment.reconfigure(
        EditorState.tabSize.of(width)
      ),
    });
  }

  /** @param {boolean} on */
  setLineWrapping(on) {
    this._editorPrefs.lineWrapping = on;
    this._view?.dispatch({
      effects: this._wrapCompartment.reconfigure(
        on ? EditorView.lineWrapping : []
      ),
    });
  }

  /** @param {boolean} on Hang continuation rows four columns in. */
  setWrapIndent(on) {
    this._editorPrefs.wrapIndent = on;
    this._view?.dispatch({
      effects: this._wrapIndentCompartment.reconfigure(on ? wrapIndent() : []),
    });
  }

  /** @param {boolean} on Mark every row that continues, at the right border. */
  setWrapArrow(on) {
    this._editorPrefs.wrapArrow = on;
    this._view?.dispatch({
      effects: this._wrapArrowCompartment.reconfigure(
        on ? wrapReturnArrows() : []
      ),
    });
  }

  /** @param {boolean} on Highlight the brace matching the one at the cursor. */
  setBraceMatching(on) {
    this._editorPrefs.braceMatching = on;
    this._view?.dispatch({
      effects: this._braceMatchCompartment.reconfigure(
        on ? bracketMatching() : []
      ),
    });
  }

  /** @param {boolean} on Offer completions while typing. */
  setAutocomplete(on) {
    this._editorPrefs.autocomplete = on;
    this._view?.dispatch({
      effects: this._autocompleteCompartment.reconfigure(
        on ? autocompletion({ override: [openscadCompletions] }) : []
      ),
    });
  }

  /** @param {boolean} on */
  setHighlightActiveLine(on) {
    this._editorPrefs.highlightActiveLine = on;
    this._view?.dispatch({
      effects: this._activeLineCompartment.reconfigure(
        on ? highlightActiveLine() : []
      ),
    });
  }

  /**
   * Re-measure the editor against its current container. CodeMirror caches
   * geometry from init time; after the panel is re-parented into a
   * different-width dock (Classic's editor slot) the cached width paints
   * wider than the pane until a measure runs.
   */
  refreshLayout() {
    if (this._view) {
      this._view.requestMeasure();
    }
  }

  /** @returns {{ start: number, end: number }} */
  getSelection() {
    if (!this._view) return { start: 0, end: 0 };
    const sel = this._view.state.selection.main;
    return { start: sel.from, end: sel.to };
  }

  /**
   * @param {number} start
   * @param {number} end
   */
  setSelection(start, end) {
    if (!this._view) return;
    this._view.dispatch({
      selection: { anchor: start, head: end },
    });
  }

  /**
   * @param {number} line - 1-based line number
   * @param {number} column - 1-based column number
   */
  setCursorPosition(line, column) {
    if (!this._view) return;
    const doc = this._view.state.doc;
    const clampedLine = Math.max(1, Math.min(line, doc.lines));
    const lineObj = doc.line(clampedLine);
    const clampedCol = Math.max(1, Math.min(column, lineObj.length + 1));
    const offset = lineObj.from + clampedCol - 1;
    this._view.dispatch({ selection: { anchor: offset } });
  }

  /**
   * @param {number} line - 1-based line number
   */
  scrollToLine(line) {
    if (!this._view) return;
    const doc = this._view.state.doc;
    const clampedLine = Math.max(1, Math.min(line, doc.lines));
    const lineObj = doc.line(clampedLine);
    this._view.dispatch({
      effects: EditorView.scrollIntoView(lineObj.from, { y: 'center' }),
    });
  }

  /**
   * @param {number[]} lineNumbers - Array of 1-based line numbers with errors
   */
  setErrorLines(lineNumbers) {
    this._errorLines = new Set(lineNumbers);
    this._applyErrorDecorations();
  }

  clearErrors() {
    this._errorLines.clear();
    this._applyErrorDecorations();
  }

  /** @private */
  _applyErrorDecorations() {
    if (!this._view) return;
    const doc = this._view.state.doc;
    const decorations = [];

    for (const lineNum of this._errorLines) {
      if (lineNum >= 1 && lineNum <= doc.lines) {
        const line = doc.line(lineNum);
        decorations.push(
          Decoration.line({ class: 'cm-error-line' }).range(line.from)
        );
      }
    }

    decorations.sort((a, b) => a.from - b.from);

    this._view.dispatch({
      effects: setErrorEffect.of(Decoration.set(decorations)),
    });
  }

  /**
   * Whether performAction(actionId) can execute in this editor.
   * Consumed by the Edit menu to disable unsupported items honestly.
   * @param {string} actionId
   * @returns {boolean}
   */
  supportsAction(actionId) {
    return Boolean(this._view) && actionId in EDITOR_COMMANDS;
  }

  /**
   * Whether there is a text edit to undo. Lets the editor toolbar disable its
   * Undo button honestly rather than offering a no-op.
   * @returns {boolean}
   */
  canUndo() {
    return Boolean(this._view) && undoDepth(this._view.state) > 0;
  }

  /**
   * Whether there is an undone text edit to redo.
   * @returns {boolean}
   */
  canRedo() {
    return Boolean(this._view) && redoDepth(this._view.state) > 0;
  }

  /**
   * Run a named editor command (Edit-menu / keyboard-shortcut integration).
   * @param {string} actionId
   * @returns {boolean} True when the command executed
   */
  performAction(actionId) {
    const command = EDITOR_COMMANDS[actionId];
    if (!command || !this._view) return false;
    this._view.focus();
    return this._runAndAnnounce(actionId, this._view);
  }

  /**
   * Run a command and speak its outcome where the outcome is otherwise
   * invisible to a screen reader — a gutter dot appearing, or a jump that
   * looks like any other cursor move.
   * @private
   */
  _runAndAnnounce(actionId, view) {
    const ran = EDITOR_COMMANDS[actionId](view);

    if (actionId === 'toggleBookmark') {
      const line = cursorLine(view.state);
      const on = bookmarkLines(view.state).includes(line);
      this.announce(
        on ? `Bookmark added, line ${line}` : `Bookmark removed, line ${line}`
      );
    } else if (actionId === 'nextBookmark' || actionId === 'previousBookmark') {
      if (ran) {
        const lines = bookmarkLines(view.state);
        const line = cursorLine(view.state);
        this.announce(
          `Line ${line}, bookmark ${lines.indexOf(line) + 1} of ${lines.length}`
        );
      } else {
        this.announce('No bookmarks in this file');
      }
    } else if (actionId === 'convertTabsToSpaces' && !ran) {
      this.announce('No tabs to convert');
    }

    return ran;
  }

  /**
   * Whether the selection covers any text — the Edit menu uses this to keep
   * Use Selection for Find honest rather than silently doing nothing.
   * @returns {boolean}
   */
  hasSelection() {
    if (!this._view) return false;
    return !this._view.state.selection.main.empty;
  }

  /**
   * Replace the current selection (or insert at the cursor) with text.
   * Used by the Edit-menu Paste handler.
   * @param {string} text
   */
  replaceSelection(text) {
    if (!this._view) return;
    this._view.dispatch(this._view.state.replaceSelection(text));
    this._view.focus();
  }

  dispose() {
    if (this._unsubscribeTheme) {
      this._unsubscribeTheme();
      this._unsubscribeTheme = null;
    }
    if (this._uiModeListener) {
      document.removeEventListener('ui-mode-changed', this._uiModeListener);
      this._uiModeListener = null;
    }
    if (this._variantObserver) {
      this._variantObserver.disconnect();
      this._variantObserver = null;
    }
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
    if (this._view) {
      this._view.destroy();
      this._view = null;
    }
    this._isInitialized = false;
  }
}
